import React, { useState } from 'react';
import DownloadExcelModal from '../modals/DownloadExcelModal';
import ExcelJS from 'exceljs';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import PropTypes from 'prop-types';
import { apiService } from '../../../../common/apiCallService';
import { fiatCurrencyList as fiatOptions } from '../../../../../client/Juno/helpers';
import { BULKPAY } from '../../constant';
import JunoButton from '../../../../../new-ui/components/common/JunoButton';
import DoneTwoToneIcon from '@mui/icons-material/DoneTwoTone';
import clsx from 'clsx';

const ALLOWED_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const REQUIRED_HEADERS = ['Amount', 'BeneficiaryName', 'Country', 'BankCountry'];

const DownloadIcon = ({ className }) => {
    return (
        <svg
            className={className}
            width="16"
            height="20"
            viewBox="0 0 16 20"
            fill="var(--color-text)"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M0 2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0H10C10.2652 5.66374e-05 10.5195 0.105451 10.707 0.293L15.707 5.293C15.8946 5.48049 15.9999 5.73481 16 6V18C16 18.5304 15.7893 19.0391 15.4142 19.4142C15.0391 19.7893 14.5304 20 14 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V2ZM13.586 6L10 2.414V6H13.586ZM8 2H2V18H14V8H9C8.73478 8 8.48043 7.89464 8.29289 7.70711C8.10536 7.51957 8 7.26522 8 7V2ZM8 9.5C8.26522 9.5 8.51957 9.60536 8.70711 9.79289C8.89464 9.98043 9 10.2348 9 10.5V13.086L9.293 12.793C9.4816 12.6108 9.7342 12.51 9.9964 12.5123C10.2586 12.5146 10.5094 12.6198 10.6948 12.8052C10.8802 12.9906 10.9854 13.2414 10.9877 13.5036C10.99 13.7658 10.8892 14.0184 10.707 14.207L8.707 16.207C8.51947 16.3945 8.26516 16.4998 8 16.4998C7.73484 16.4998 7.48053 16.3945 7.293 16.207L5.293 14.207C5.19749 14.1148 5.12131 14.0044 5.0689 13.8824C5.01649 13.7604 4.9889 13.6292 4.98775 13.4964C4.9866 13.3636 5.0119 13.2319 5.06218 13.109C5.11246 12.9861 5.18671 12.8745 5.28061 12.7806C5.3745 12.6867 5.48615 12.6125 5.60905 12.5622C5.73194 12.5119 5.86362 12.4866 5.9964 12.4877C6.12918 12.4889 6.2604 12.5165 6.3824 12.5689C6.50441 12.6213 6.61475 12.6975 6.707 12.793L7 13.086V10.5C7 10.2348 7.10536 9.98043 7.29289 9.79289C7.48043 9.60536 7.73478 9.5 8 9.5Z" />
        </svg>
    );
};

const UploadBulkPayExcel = ({
    handleContinue,
    handleChangeBulkpaymentId,
    imageBaseUrl,
    clientData,
    userDetails,
    apiUrl,
}) => {
    const [fileState, setFileState] = useState({
        file: null,
        error: {
            message: '',
            errors: [],
        },
        loading: false,
        successMessage: '',
    });

    const [downloadTemplate, setDownloadTemplate] = useState(false);
    const { clientObjId, accountID, clientEmail, clientId } = clientData;
    const { email, adminRole } = userDetails;

    const handleFileChange = async event => {
        setFileState(prev => ({
            ...prev,
            file: null,
            error: {
                message: '',
                errors: [],
            },
            successMessage: '',
            loading: true,
        }));

        const selectedFile = event.target.files[0];
        if (!selectedFile) {
            setFileState(prev => ({
                ...prev,
                loading: false,
                error: { ...prev.error, message: 'No file selected.' },
            }));
            return;
        }

        if (selectedFile.type !== ALLOWED_TYPE) {
            setFileState(prev => ({
                ...prev,
                loading: false,
                error: {
                    ...prev.error,
                    message: 'Invalid file type. Only .xlsx files are allowed.',
                },
            }));
            return;
        }

        if (selectedFile.size > MAX_SIZE) {
            setFileState(prev => ({
                ...prev,
                loading: false,
                error: {
                    ...prev.error,
                    message: 'File size exceeds the maximum limit of 10MB.',
                },
            }));
            return;
        }

        try {
            const workbook = new ExcelJS.Workbook();
            const buffer = await selectedFile.arrayBuffer();
            await workbook.xlsx.load(buffer);

            const worksheet = workbook.getWorksheet(BULKPAY.mainSheet);
            if (!worksheet) {
                setFileState(prev => ({
                    ...prev,
                    loading: false,
                    error: {
                        ...prev.error,
                        message:
                            'Failed upload - Please download our template file to process payments',
                    },
                }));
                return;
            }

            const rows = [];
            worksheet.eachRow((row, rowNumber) => {
                const rowValues = row.values;
                rowValues.shift();
                if (rowValues.some(row => row?.toString()?.trim())) {
                    rows.push(rowValues?.map(row => row?.toString()?.trim()?.toLowerCase()));
                }
            });

            if (rows.length > 101) {
                setFileState(prev => ({
                    ...prev,
                    loading: false,
                    error: {
                        ...prev.error,
                        message: `${
                            rows.length - 1
                        } transactions exceeds the 100 transactions per bulk upload limit.`,
                    },
                }));
                return;
            }

            const headers = rows[0];
            const missingHeaders = REQUIRED_HEADERS.filter(
                header => !headers.includes(header.trim().toLowerCase()),
            );

            if (missingHeaders.length > 0) {
                setFileState(prev => ({
                    ...prev,
                    loading: false,

                    error: {
                        ...prev.error,
                        message: `Invalid template. Missing headers: ${missingHeaders.join(', ')}`,
                    },
                }));
                return;
            }

            // validate required fields
            let errorList = [];
            let currencies = {};
            const fiats = new Set(fiatOptions.map(fiat => fiat.text));
            rows.forEach((row, index) => {
                if (!index) return;
                if (!row[0]) {
                    errorList.push(`Error Line ${index}, Beneficiary Name is missing`);
                }

                if (!row[8]) {
                    errorList.push(`Error Line ${index}, Beneficiary County is missing`);
                }

                if (!row[11]) {
                    errorList.push(`Error Line ${index}, Bank Country is missing`);
                }

                if (row[1] != undefined && row[1].length) {
                    errorList.push(`Error Line ${index}, Please leave Unique Reference Blank`);
                }

                if (row[21] == undefined) {
                    errorList.push(`Error Line ${index}, Amount is missing`);
                }

                if (
                    row[21] != undefined &&
                    String(row[21]).includes('.') &&
                    String(row[21]).split('.').pop().length > 2
                ) {
                    errorList.push(
                        `Error Line ${index}, Amount value must be formatted to maximum of two decimal places`,
                    );
                }

                if (!row[9]) {
                    errorList.push(`Error Line ${index}, Payment currency is missing`);
                }

                if (!fiats.has(row[9]?.toUpperCase())) {
                    errorList.push(`Error Line ${index}: Payment currency is invalid`);
                }

                if (!fiats.has(row[19]?.toUpperCase())) {
                    errorList.push(`Error Line ${index}: Settlement currency is invalid`);
                }

                if (row[9] != row[19]) {
                    errorList.push(
                        `Error Line ${index}: Payment currency must match Settlement Currency`,
                    );
                }

                if (row[0]?.length > 35) {
                    errorList.push(
                        `Error Line ${index}: Beneficiary name maximum of 35 characters`,
                    );
                }

                if (row[2]?.length > 35) {
                    errorList.push(
                        `Error Line ${index}: Account holder name maximum of 35 characters`,
                    );
                }

                const descMapper = {
                    12: 'Account Number',
                    13: 'SWIFT',
                    14: 'IBAN',
                    15: 'Other Routing',
                };

                const noSymbolRows = [12, 13, 14, 15];

                noSymbolRows.forEach(position => {
                    if (row[position]?.includes('-')) {
                        errorList.push(
                            `Error Line ${index}: Remove symbols from the ${descMapper[position]}`,
                        );
                    }
                });

                if (row[20]?.length > 140) {
                    errorList.push(
                        `Error Line ${index}: Payment reference maximum of 140 characters`,
                    );
                }

                if (row[20]?.length) {
                    let validReference = /^[A-Za-z0-9\s\-\/\?:\(\)\.,'\+]+$/;
                    let test = validReference.test(row[20]);
                    if (!test) {
                        errorList.push(
                            `Error Line ${index}: Payment reference contains invalid characters. Acceptable characters are numbers, letters, space and / - ? : ( ) . , ' +`,
                        );
                    }
                }

                if (!row[20] || !row[20]?.trim()?.length) {
                    errorList.push(`Error Line ${index}: Payment reference is missing`);
                }

                if (!row[3] || !row[3]?.trim()?.length) {
                    errorList.push(`Error Line ${index}: Address1 is missing`);
                }

                let currency = row[9]?.toUpperCase();
                if (currencies[currency] == undefined) {
                    currencies[currency] = { amount: 0, lines: [] };
                }

                currencies[currency].amount += parseFloat(row[21]);
                currencies[currency].lines.push(index);
            });

            let [clientFees, clientBalances] = await Promise.all([
                new Promise((res, rej) => {
                    apiService(
                        (window.location.hostname === 'localhost' ? apiUrl : '') +
                            '/restapi/get-client-fee',
                        {
                            clientId,
                            transactionType: 'Payment Out',
                            currencyType: 'fiat',
                            fromCurrencies: Object.keys(currencies),
                        },
                        data => res(data),
                        error => rej(error),
                    );
                }),
                new Promise((res, rej) => {
                    apiService(
                        (window.location.hostname === 'localhost' ? apiUrl : '') +
                            '/restapi/get-client-balances',
                        {
                            clientId,
                            email,
                            isWebUI: true,
                        },
                        data => res(data),
                        error => rej(error),
                    );
                }),
            ]);

            Object.entries(currencies)?.forEach(([currency, { amount, lines }]) => {
                let fee = clientFees?.find(fee => fee.fromCurrency == currency)?.percentFee || 0;
                let balance =
                    clientBalances?.clientBalanceList.find(
                        balance => balance.currencyShortName == currency,
                    )?.balanceAmount || 0;

                let feeAmount = parseFloat(((amount * fee) / 100).toFixed(2));

                if (balance < amount + feeAmount) {
                    errorList.push(`Error Line ${lines}: Insufficient funds available`);
                }
            });

            if (errorList.length) {
                return setFileState(prev => ({
                    ...prev,
                    file: null,
                    error: {
                        ...prev.error,
                        errors: errorList,
                        message: 'Validation failed. Click here to download the error details.',
                    },
                    loading: false,
                }));
            }

            setFileState(prev => ({
                ...prev,
                file: selectedFile,
                loading: false,
            }));
        } catch (err) {
            console.error('Excel parse error:', err);
            setFileState(prev => ({
                ...prev,
                file: null,
                error: {
                    ...prev.error,
                    message: 'Unable to read Excel file. Please upload a valid .xlsx file.',
                },
                loading: false,
            }));
        }

        event.target.value = '';
    };

    const uploadFile = async () => {
        if (!fileState.file) {
            setFileState(prev => ({
                ...prev,
                error: { ...prev.error, message: 'No file selected.' },
            }));
            return;
        }

        setFileState(prev => ({
            ...prev,
            error: {
                message: '',
                errors: [],
            },
            successMessage: '',
            loading: true,
        }));

        const formData = new FormData();
        formData.append('adminRole', adminRole);
        formData.append('email', email);
        formData.append('client', clientObjId);
        formData.append('accountID', accountID);
        formData.append('clientEmail', clientEmail);
        formData.append('file', fileState.file);
        formData.append('clientId', clientId);

        try {
            await apiService(
                (window.location.hostname === 'localhost' ? apiUrl : '') +
                    '/restapi/bulk/upload-csv',
                formData,
                data => {
                    handleChangeBulkpaymentId(data.bulkPaymentId, data.transactionId);
                    setFileState(prev => ({
                        ...prev,
                        successMessage: 'File uploaded successfully!',
                    }));
                    handleContinue();
                },
                (errorMessage, details = []) => {
                    setFileState(prev => ({
                        ...prev,
                        error: {
                            message: details.length
                                ? 'Upload failed. Click here to download the error details.'
                                : errorMessage || 'Upload failed.',
                            errors: details,
                        },
                    }));
                },
            );
        } catch (err) {
            console.log(err);
            setFileState(prev => ({
                ...prev,
                error: {
                    message: 'Upload failed.',
                },
            }));
        } finally {
            setFileState(prev => ({
                ...prev,
                loading: false,
            }));
        }
    };

    const downloadErrorCSV = errors => {
        const errorData = errors.map((msg, idx) => ({
            'Error Number': idx + 1,
            'Error Message': msg,
        }));

        const csvExporter = new ExportToCsv({
            filename: 'upload_errors',
            fieldSeparator: ',',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            useBom: true,
            useKeysAsHeaders: true,
        });

        csvExporter.generateCsv(errorData);
    };

    return (
        <div className="flex flex-col items-center gap-9 w-full md:w-[560px] p-6 rounded-lg outline outline-1 outline-offset-[-1px] outline-bulkpay-card-outline bg-bulkpay-card-bg">
            <div className="flex flex-col items-start gap-2 self-stretch">
                <p className="text-text md:text-xl text-lg font-semibold uppercase leading-loose tracking-tight">
                    Upload Bulk Pay Excel
                </p>
                <p className="text-balanceCard-manage font-inter font-medium text-sm uppercase leading-tight">
                    For best results, complete and upload our bulk pay Excel template provided
                    below.
                </p>
            </div>

            <div className="flex flex-col items-start gap-2 self-stretch">
                <p className="text-balanceCard-manage leading-none uppercase font-inter text-xs font-medium">
                    Bulk Payments<span className="text-balanceCard-manage font-medium ml-px">*</span>
                </p>

                <div className="inline-flex justify-start items-center gap-3 p-[.7rem] self-stretch rounded-lg  outline outline-1 outline-offset-[-1px] outline-input-border bg-[#fafafa] dark:bg-[#3F3F46]">
                    <input
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                        onClick={event => {
                            event.target.value = null;
                        }}
                        className="hidden"
                        id="fileUpload"
                        disabled={fileState.loading}
                    />

                    <label
                        htmlFor="fileUpload"
                        data-e2e="choose-file-btn"
                        className="cursor-pointer whitespace-nowrap min-w-min bg-[#E4E4E7] text-black leading-none uppercase text-xs p-[.65rem] rounded-md text-center">
                        Choose File
                    </label>

                    <span
                        className={clsx(
                            'text-sm leading-tight font-medium uppercase truncate overflow-hidden',
                            fileState.file ? 'text-text' : 'text-balanceCard-manage',
                        )}>
                        {fileState.file ? fileState.file.name : 'no file chosen'}
                    </span>

                    {fileState.file && <DoneTwoToneIcon className="text-text self-center" />}
                </div>

                {fileState.error?.message && (
                    <p
                        className={`text-red-500 text-sm ${
                            fileState.error?.errors?.length > 0
                                ? 'cursor-pointer underline hover:text-red-600'
                                : 'cursor-not-allowed text-red-400'
                        }`}
                        onClick={() =>
                            fileState.error?.errors?.length > 0 &&
                            downloadErrorCSV(fileState.error?.errors)
                        }>
                        {fileState.error?.message}
                    </p>
                )}
                {fileState.successMessage && (
                    <p className="text-green-500 text-sm">{fileState.successMessage}</p>
                )}
            </div>

            <JunoButton
                type="primary"
                disabled={
                    fileState.loading ||
                    !!fileState.error?.errors?.length ||
                    !!fileState.error?.message ||
                    !fileState.file
                }
                onClick={uploadFile}
                data-e2e={fileState.loading ? 'Uploading...' : 'Continue'}>
                {fileState.loading ? 'Uploading...' : 'Continue'}
            </JunoButton>

            <div className="flex flex-col items-start gap-6 self-stretch">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setDownloadTemplate(true)}>
                    <DownloadIcon className="!fill-text" data-e2e="download-templet"/>
                    <p className="self-end text-sm font-medium text-text leading-tight uppercase underline">
                        Download template Excel file
                    </p>
                </div>
                <p className="text-balanceCard-manage text-sm uppercase leading-tight font-medium">
                    Make sure you complete all mandatory fields before uploading, otherwise your
                    payment won’t be processed.
                </p>
            </div>

            {/* CSV Download Modal */}
            <DownloadExcelModal
                open={downloadTemplate}
                handleClose={() => setDownloadTemplate(false)}
                name="bulkPaymentTemplate"
            />
        </div>
    );
};

UploadBulkPayExcel.propTypes = {
    handleContinue: PropTypes.func.isRequired,
    handleChangeBulkpaymentId: PropTypes.func.isRequired,
    clientData: PropTypes.shape({
        clientObjId: PropTypes.string,
        accountNumber: PropTypes.string,
        clientEmail: PropTypes.string,
        clientId: PropTypes.string,
    }).isRequired,
    imageBaseUrl: PropTypes.string.isRequired,
    userDetails: PropTypes.shape({
        email: PropTypes.string,
        adminRole: PropTypes.string,
    }).isRequired,
};

export default UploadBulkPayExcel;
